<template>
  <div>
    <!--
      Rendered outside InstantSearchContainer (which is ClientOnly) so the
      heading is part of the SSR markup crawlers see. Only shown when the
      customer configured one — there was no heading here before (JET-831).
    -->
    <div
      v-if="h1"
      class="search-page-heading-bar search-filter-page"
      :class="themeClass"
      :style="colorStyles"
    >
      <h1 class="search-page-heading">
        {{ h1 }}
      </h1>
    </div>

    <InstantSearchContainer />
  </div>
</template>

<script setup lang="ts">
// Track page
usePageTracking()

const { t } = useI18n()
const { data: jobShop } = useNuxtData<JobShop>("jobShopData")
const jobSearchGlobal = computed(() => jobShop.value?.search)

// Customisable SEO title, description and heading (JET-831). Empty values fall
// back to the previous behaviour, so tenants without configuration are unchanged.
// The layout resolves i18n to the jobshop's own locale before pages render, so
// the fallback title comes out in the jobshop language rather than the visitor's.
const { title, description, h1 } = useSearchPageSeo(t)

useServerSeoMeta({
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value,
})

// Match the search area below so the heading bar blends into it.
const themeClass = computed(() => `theme-${jobSearchGlobal.value?.config.general.theme?.value || ""}`)

const colorStyles = computed(() => {
  const general = jobSearchGlobal.value?.config.general
  if (!general)
    return {}

  return {
    "--search-filter-accent-color": general.accentColor.value,
    "--search-filter-bg-color": general.pageBackgroundColor.value,
  }
})

// Self-referencing canonical URL (JET-2331)
const canonicalUrl = useCanonicalUrl("search")
useServerHead(() => ({
  link: [{ rel: "canonical", href: canonicalUrl.value }],
}))
</script>

<style lang="scss" scoped>
.search-page-heading-bar {
  // Crossroads' breakpoint mixins are container queries, not media queries — they
  // resolve against the nearest ancestor with `container-type`. The search UI gets
  // that from `.ais-InstantSearch`, which this heading sits outside of, so it has
  // to open its own containment context or `scale-fonts` / `content-wrapper` would
  // be stuck on their widest values at every viewport size.
  container-type: inline-size;

  // `.search-filter-page` is a full-page container; here it only supplies the
  // theme variables and background colour for the heading strip.
  height: auto;
}

.search-page-heading {
  @include content-wrapper;
  @include font-family("heading");
  @include scale-fonts("heading-1");

  width: 100%;

  // Fluid against the heading bar's inline size (see container-type above), so the
  // gap above the heading shrinks with the font instead of staying at its widest.
  padding-top: clamp(24px, 3cqi, 40px);

  font-style: normal;
  hyphens: auto;
  color: var(--search-filter-text-color);
  overflow-wrap: anywhere;
}
</style>
